import React, { useEffect, useState } from "react";
import { Dropdown, DropdownButton, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGetInboundCampaignMessagingDataQuery } from "../store/slices/widgetsSlice.ts";
import { useGetInboundCampaignsQuery } from "../store/inboundCampaignSlice";
import {
  faAngleDown,
  faAngleUp,
  faMessage,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import MyModal from "../Common/MyModal";
import { Link } from "react-router-dom";
import MyPagination from "../Common/MyPagination";
import InboundCampaignForm from "../Inbounds/InboundCampaignForm";
import SelectInput from "../Builder/nodes/Logic/Inputs/SelectInput";
import useDebouncedValue from "../Common/useDebouncedValue";

const columnOptions = [
  { value: "Date", label: "Date" },
  { value: "Channel", label: "Channel" },
  { value: "From", label: "From" },
  { value: "To", label: "To / Handlers" },
  { value: "Content", label: "Content" },
];

const CONTENT_PREVIEW_LENGTH = 200;

function isHtmlContent(content) {
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

function htmlToPlainText(content) {
  if (typeof document === "undefined") {
    return content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  const container = document.createElement("div");
  container.innerHTML = content;
  return (container.textContent || "").replace(/\s+/g, " ").trim();
}

function getContentPreview(content) {
  if (content.length <= CONTENT_PREVIEW_LENGTH) {
    return { text: content, trimmed: false };
  }

  const boundary = content.slice(CONTENT_PREVIEW_LENGTH).search(/[.!?,;:\s]/);
  const end =
    boundary === -1 ? CONTENT_PREVIEW_LENGTH : CONTENT_PREVIEW_LENGTH + boundary + 1;

  return { text: content.slice(0, end).trimEnd(), trimmed: true };
}

function InboundContent({ channel, content, subject }) {
  const originalContent = content || "";
  const isHtml = isHtmlContent(originalContent);
  const plainText = isHtml ? htmlToPlainText(originalContent) : originalContent;
  const preview = getContentPreview(plainText);
  const canShowMore = preview.trimmed || isHtml;
  const showSubject = channel === "email" && subject;

  return (
    <>
      {showSubject && (
        <div className="mb-1">
          <strong>Subject:</strong> {subject}
        </div>
      )}
      <span>{preview.text || "—"}</span>
      {canShowMore && (
        <MyModal
          label="Show more"
          buttonClassName="btn-link ms-2"
          heading="Message content"
          size="xl"
        >
          {showSubject && (
            <p>
              <strong>Subject:</strong> {subject}
            </p>
          )}
          {isHtml ? (
            <iframe
              title="Full message content"
              className="border-0 w-100"
              sandbox=""
              srcDoc={originalContent}
              style={{ minHeight: "70vh" }}
            />
          ) : (
            <div className="text-break" style={{ whiteSpace: "pre-wrap", minHeight: "50vh" }}>
              {originalContent}
            </div>
          )}
        </MyModal>
      )}
    </>
  );
}

function HandlerCampaign({ hit, inboundAt }) {
  const campaign = hit.Handler;

  if (!campaign?.Label) {
    return "—";
  }

  return (
    <MyModal
      label={campaign.Label}
      buttonClassName="btn-link text-start"
      heading={campaign.Label}
    >
      <h6>Campaign details</h6>
      <Table bordered responsive size="sm">
        <tbody>
          <tr>
            <th scope="row">Name</th>
            <td>{campaign.Label}</td>
          </tr>
          <tr>
            <th scope="row">Reference</th>
            <td>{campaign.Reference || "—"}</td>
          </tr>
          <tr>
            <th scope="row">Description</th>
            <td>{campaign.Description || "—"}</td>
          </tr>
          <tr>
            <th scope="row">Campaign</th>
            <td>
              <InboundCampaignForm
                uuid={campaign.Uuid || hit.HandlerUuid}
                showButton={
                  <button type="button" className="btn btn-sm btn-colored">
                    View campaign
                  </button>
                }
              />
            </td>
          </tr>
          <tr>
            <th scope="row">Message date</th>
            <td>{hit.CreatedAt ? new Date(hit.CreatedAt).toLocaleString() : "—"}</td>
          </tr>
          <tr>
            <th scope="row">Message channel</th>
            <td>{hit.Channel || "—"}</td>
          </tr>
          <tr>
            <th scope="row">Message sender</th>
            <td>{hit.From || "—"}</td>
          </tr>
          <tr>
            <th scope="row">Message type</th>
            <td>{hit.Kind || "—"}</td>
          </tr>
          <tr>
            <th scope="row">Generated outbound</th>
            <td>
              <Link
                to={`/reporting/realtime/inbound?inboundUUID=${hit.HandlerUuid}&startDate=${inboundAt}`}
                className="btn btn-sm btn-colored"
                title="View generated outbound transactions"
                aria-label="View generated outbound transactions"
              >
                <FontAwesomeIcon icon={faMessage} />
              </Link>
            </td>
          </tr>
        </tbody>
      </Table>
    </MyModal>
  );
}

export default function InboundCampaignMessaging() {
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [pagination, setPagination] = useState({ offset: 0, limit: 10 });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => {
    return localStorage.getItem("InboundMessagingOverviewFiltersExpanded") === "true";
  });
  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem("InboundMessagingOverviewFilters");
      const parsed = stored ? JSON.parse(stored) : {};
      return {
        handler_uuid: Array.isArray(parsed.handler_uuid) ? parsed.handler_uuid : [],
        channel: Array.isArray(parsed.channel) ? parsed.channel : [],
        date_gte: parsed.date_gte || "",
        date_lte: parsed.date_lte || "",
        search: parsed.search || "",
        search_field: Array.isArray(parsed.search_field) && parsed.search_field.length
          ? parsed.search_field
          : ["sender", "recipient", "subject", "campaign", "content"],
      };
    } catch {
      return {
        handler_uuid: [],
        channel: [],
        date_gte: "",
        date_lte: "",
        search: "",
        search_field: ["sender", "recipient", "subject", "campaign", "content"],
      };
    }
  });
  const [columnsToShow, setColumnsToShow] = useState(() => {
    const defaultColumns = ["report", "Date", "Channel", "From", "To", "Content"];

    try {
      const stored = localStorage.getItem("InboundMessagingOverviewColumns");
      return stored ? JSON.parse(stored).filter((column) => column !== "Handlers") : defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const debouncedSearch = useDebouncedValue(filters.search);

  const { data: campaignsData = { items: [] } } = useGetInboundCampaignsQuery({
    limit: 100,
    offset: 0,
  });
  const campaignOptions = (campaignsData.items || [])
    .filter((campaign) => campaign.Uuid)
    .map((campaign) => ({
      value: campaign.Uuid,
      label: campaign.Label,
      details: [campaign.Reference, campaign.Channel, campaign.Inbound]
        .filter(Boolean)
        .join(", "),
    }));
  const { data = { summary: [], total: 0 }, refetch } =
    useGetInboundCampaignMessagingDataQuery({
      order_by: orderBy,
      order,
      ...filters,
      search: debouncedSearch,
      ...pagination,
    });

  useEffect(() => {
    localStorage.setItem("InboundMessagingOverviewFilters", JSON.stringify(filters));
  }, [filters]);

  const updateFilters = (changes) => {
    setFilters((previous) => ({ ...previous, ...changes }));
    setPagination((previous) => ({ ...previous, offset: 0 }));
  };

  const changeSort = (column) => {
    if (column === orderBy) {
      setOrder((previous) => (previous === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(column);
      setOrder("asc");
    }
    setPagination((previous) => ({ ...previous, offset: 0 }));
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3 gap-3 flex-wrap">
        <button
          type="button"
          className="btn btn-colored"
          aria-expanded={showAdvancedFilters}
          onClick={() => {
            setShowAdvancedFilters((previous) => {
              localStorage.setItem("InboundMessagingOverviewFiltersExpanded", String(!previous));
              return !previous;
            });
          }}
        >
          Filters
        </button>
        <DropdownButton
          id="show-colums-dropdown"
          title="Show columns"
          variant="colored"
          autoClose="outside"
          align="end"
          className="h-100 rounded-3"
        >
          {columnOptions.map((c) => (
            <Dropdown.Item as="div" key={c.value}>
              <Form.Check
                checked={columnsToShow.includes(c.value)}
                label={c.label}
                id={`${c.value}-checkbox`}
                onChange={() => {
                  setColumnsToShow((p) => {
                    let newColumns;
                    if (p.includes(c.value)) {
                      newColumns = p.filter((pc) => pc !== c.value);
                    } else {
                      newColumns = [...p, c.value];
                    }
                    localStorage.setItem(
                      "InboundMessagingOverviewColumns",
                      JSON.stringify(newColumns)
                    );
                    return newColumns;
                  });
                }}
              />
            </Dropdown.Item>
          ))}
        </DropdownButton>
        <button
          type="button"
          className="btn btn-sm border-0"
          onClick={refetch}
          title="Refresh"
        >
          <FontAwesomeIcon icon={faRotateLeft} />
        </button>
      </div>
      {showAdvancedFilters && (
        <div className="asset-filter-panel mb-3 bg-colored-reverse p-3 m-0 rounded-3 text-white">
          <div className="d-flex flex-column gap-3">
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>
                Search
              </div>
              <div className="d-flex flex-column gap-2 flex-grow-1">
                <Form.Control
                  placeholder="Search selected fields"
                  value={filters.search}
                  onChange={(event) => updateFilters({ search: event.target.value })}
                />
                <div className="d-flex flex-wrap gap-3">
                  {[
                    ["sender", "Sender"],
                  ["recipient", "Inbound"],
                  ["subject", "Inbound subject"],
                    ["campaign", "Campaign"],
                  ["content", "Inbound message"],
                  ].map(([value, label]) => (
                    <Form.Check
                      inline
                      key={value}
                      type="checkbox"
                      id={`inbound-search-${value}`}
                      label={label}
                      checked={filters.search_field.includes(value)}
                      onChange={() => updateFilters({
                        search_field: filters.search_field.includes(value)
                          ? filters.search_field.filter((field) => field !== value)
                          : [...filters.search_field, value],
                      })}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>
                Campaigns
              </div>
              <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
                <div style={{ minWidth: 260, flex: "1 1 420px" }}>
                  <SelectInput
                    key={campaignOptions.map((campaign) => campaign.value).join(",") || "loading"}
                    value={filters.handler_uuid}
                    onChange={(handler_uuid) => updateFilters({ handler_uuid })}
                    options={campaignOptions}
                    formatOptionLabel={(campaign) => (
                      <div>
                        <div>{campaign.label}</div>
                        {campaign.details && <small className="opacity-75">({campaign.details})</small>}
                      </div>
                    )}
                    isMulti
                    isClearable
                    placeholder="Select campaigns"
                  />
                </div>
              </div>
            </div>
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>
                Date
              </div>
              <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
                <Form.Group style={{ width: 250, maxWidth: "100%" }}>
                  <Form.Label className="small mb-1">From</Form.Label>
                  <Form.Control
                    aria-label="Date from"
                    type="datetime-local"
                    value={filters.date_gte}
                    onChange={(event) => updateFilters({ date_gte: event.target.value })}
                  />
                </Form.Group>
                <Form.Group style={{ width: 250, maxWidth: "100%" }}>
                  <Form.Label className="small mb-1">To</Form.Label>
                  <Form.Control
                    aria-label="Date to"
                    type="datetime-local"
                    value={filters.date_lte}
                    onChange={(event) => updateFilters({ date_lte: event.target.value })}
                  />
                </Form.Group>
              </div>
            </div>
            <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
              <div className="fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center" style={{ width: 130 }}>
                Channel
              </div>
              <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
                <div style={{ width: 420, maxWidth: "100%" }}>
                  <SelectInput
                    value={filters.channel}
                    onChange={(channel) => updateFilters({ channel })}
                    options={[
                      { value: "email", label: "Email" },
                      { value: "sms", label: "SMS" },
                      { value: "voice", label: "Voice" },
                    ]}
                    isMulti
                    isClearable
                    placeholder="Select channels"
                  />
                </div>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() =>
                  updateFilters({
                    handler_uuid: [],
                    channel: [],
                    date_gte: "",
                    date_lte: "",
                    search: "",
                    search_field: ["sender", "recipient", "subject", "campaign", "content"],
                  })
                }
              >
                Clear filters
              </button>
            </div>
          </div>
        </div>
      )}
      <div>
        <Table striped bordered hover>
          <thead>
            <tr>
              {[
                ["Date", "created_at"],
                ["Channel", "channel"],
                ["From", "from"],
                ["To", "to", "To / Handlers"],
              ].map(([value, column, label = value]) =>
                columnsToShow.includes(value) ? (
                  <th key={column}>
                    <button className="btn btn-link p-0" type="button" onClick={() => changeSort(column)}>
                      {label}
                    </button>
                    {orderBy === column && (
                      <FontAwesomeIcon className="ms-2" icon={order === "asc" ? faAngleUp : faAngleDown} />
                    )}
                  </th>
                ) : null
              )}
              {columnsToShow.includes("Content") && <th>Content</th>}
            </tr>
          </thead>
          <tbody>
            {data.summary.map((item, i) => (
              <tr key={i}>
                {columnsToShow.includes("Date") && (
                  <td>
                    {/* <MyModal
                    size="lg"
                    label={item.id}
                    buttonClassName="btn btn-link"
                    modalClassName="bg-purple-gradient"
                  >
                    <div>
                      {item.channel === "email" && (
                        <div>
                          <h3>Subject</h3>
                          {item.subject}
                        </div>
                      )}
                      <div>
                        <h3>Content</h3>
                        {item.content}
                      </div>
                    </div>
                  </MyModal> */}
                    {new Date(item.at).toLocaleString()}
                  </td>
                )}
                {columnsToShow.includes("Channel") && <td>{item.channel}</td>}
                {columnsToShow.includes("From") && <td>{item.from}</td>}
                {columnsToShow.includes("To") && (
                  <td>
                    <div>{item.inbound}</div>
                    {item.hits.length > 0 && (
                      <div className="mt-2">
                        {item.hits.map((hit, idx) => (
                          <div key={idx}>
                            <HandlerCampaign hit={hit} inboundAt={item.at} />
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                )}
                {columnsToShow.includes("Content") && (
                  <td>
                    <InboundContent
                      channel={item.channel}
                      content={item.content}
                      subject={item.subject}
                    />
                  </td>
                )}
              </tr>
            ))}
            {!data.summary.length && (
              <tr>
                <td
                  className="text-center text-secondary"
                  colSpan={
                    columnOptions.filter((c) => columnsToShow.includes(c.value))
                      .length
                  }
                >
                  No inbound triggers found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <MyPagination
          total={data.total}
          onPaginate={(page, perPage) => {
            setPagination({ offset: page * perPage, limit: perPage });
          }}
          perPageOptions={[10, 25, 50]}
          defaultPerPage={10}
          alwaysVisible
        />
      </div>
    </>
  );
}
